/**
 * The words around publishing: the top bar's pill, the review page's groups and its button, a
 * history entry's summary, and the activity feed. Pure (type imports only), unit-tested.
 */

import type { AuditEntry, EntityType, PendingChange, PublishRecord } from "../api/admin";
import type { Pack } from "../vocabulary/vocabulary";

const plural = (count: number, one: string, many: string) => `${count} ${count === 1 ? one : many}`;

/** The pill in the top bar: "3 changes not live" or "All changes are live". */
export function pillText(count: number): string {
  return count === 0 ? "All changes are live" : `${plural(count, "change", "changes")} not live`;
}

/** The review page's button names the action (GOV.UK: a button says what it does). */
export function publishButtonLabel(count: number): string {
  return count === 0 ? "Nothing to publish" : `Publish ${plural(count, "change", "changes")} to your app`;
}

export interface GroupedChanges {
  added: PendingChange[];
  updated: PendingChange[];
  removed: PendingChange[];
}

export function groupChanges(changes: PendingChange[]): GroupedChanges {
  return {
    added: changes.filter((c) => c.change === "add"),
    updated: changes.filter((c) => c.change === "update"),
    removed: changes.filter((c) => c.change === "remove"),
  };
}

export interface ItemsDiff {
  added: number;
  updated: number;
  removed: number;
}

/** What one published version changed against the one before it ("title:t-x" → revision maps). */
export function diffItems(current: Record<string, number> | null, previous: Record<string, number> | null): ItemsDiff | null {
  if (!current) return null;
  const before = previous ?? {};
  let added = 0;
  let updated = 0;
  let removed = 0;
  for (const [key, revision] of Object.entries(current)) {
    if (before[key] === undefined) added++;
    else if (before[key] !== revision) updated++;
  }
  for (const key of Object.keys(before)) if (current[key] === undefined) removed++;
  return { added, updated, removed };
}

/** "12 added, 3 updated, 1 removed" or "No changes to items". */
export function diffText(diff: ItemsDiff): string {
  const parts = [diff.added ? `${diff.added} added` : "", diff.updated ? `${diff.updated} updated` : "", diff.removed ? `${diff.removed} removed` : ""].filter(Boolean);
  return parts.length ? parts.join(", ") : "No changes to items";
}

export type HistoryKind = "publish" | "restore" | "refresh";

/**
 * One line for a history entry. `previous` is the version published just before it (the list is
 * newest first, so the next element), or null for the first ever.
 */
export function historySummary(record: Pick<PublishRecord, "kind" | "sourceVersion" | "items">, previous: Pick<PublishRecord, "items"> | null, sourceDate?: string): { kind: HistoryKind; title: string; detail: string } {
  if (record.kind === "refresh") return { kind: "refresh", title: "Live times updated", detail: "The apps were told which live events have started or ended. Nothing else changed." };
  const diff = diffItems(record.items, previous?.items ?? null);
  if (record.kind === "rollback") {
    return { kind: "restore", title: "Restored an earlier version", detail: `${sourceDate ? `Put back the version from ${sourceDate}` : "Put back an earlier version"}${diff ? `: ${diffText(diff).toLowerCase()}` : ""}.` };
  }
  return { kind: "publish", title: previous ? "Published" : "First publish", detail: diff ? `${diffText(diff)}.` : "Published before changes were recorded." };
}

/** The noun for a change's kind of item, or a plain word for ones the vocabulary does not cover. */
export function kindWord(pack: Pack, type: string): string {
  return (pack.nouns as Record<string, { one: string } | undefined>)[type]?.one ?? "Item";
}

const ACTIONS: Record<string, (who: string, what: string, entry: AuditEntry) => string> = {
  "entity.create": (who, what) => `${who} added ${what}`,
  "entity.save": (who, what) => `${who} saved ${what}`,
  "entity.delete": (who, what) => `${who} deleted ${what}`,
  "entity.restore": (who, what) => `${who} went back to an earlier version of ${what}`,
  publish: (who) => `${who} published to the app`,
  rollback: (who) => `${who} restored an earlier version of the app`,
  import: (who, _what, e) => `${who} imported a catalogue${typeof e.detail?.["created"] === "number" ? ` (${e.detail["created"]} new, ${e.detail["updated"] ?? 0} updated)` : ""}`,
  "brand.save": (who) => `${who} saved the brand`,
  "settings.save": (who) => `${who} changed the default language`,
  "preview.token": (who) => `${who} made a preview link`,
  "media.upload": (who, _what, e) => `${who} uploaded a video${e.detail?.["title"] ? ` “${String(e.detail["title"])}”` : ""}`,
  "media.image": (who, _what, e) => `${who} uploaded an image${e.detail?.["title"] ? ` “${String(e.detail["title"])}”` : ""}`,
  "media.delete": (who) => `${who} deleted a file`,
  "session.sign_in": (who) => `${who} signed in`,
  "staff.add": (who, _what, e) => `${who} added ${String(e.detail?.["email"] ?? "someone")} to the team`,
  "staff.cli": (_who, _what, e) => `${String(e.detail?.["email"] ?? "Someone")} was added to the team from the command line`,
  "staff.role": (who, _what, e) => `${who} made ${String(e.detail?.["email"] ?? "someone")} ${article(String(e.detail?.["role"] ?? "member"))}`,
  "staff.remove": (who, _what, e) => `${who} removed ${String(e.detail?.["email"] ?? "someone")} from the team`,
  "voucher.batch": (who, _what, e) => `${who} made ${String(e.detail?.["count"] ?? "")} ticket codes${e.detail?.["label"] ? ` “${String(e.detail["label"])}”` : ""}`.replace("  ", " "),
  "voucher.revoke": (who) => `${who} cancelled unused ticket codes`,
  "guests.add": (who, _what, e) => `${who} added ${String(e.detail?.["added"] ?? "")} people to a guest list`.replace("  ", " "),
  "grant.comp": (who, _what, e) => `${who} gave free access to ${String(e.detail?.["email"] ?? "someone")}`,
  "grant.revoke": (who) => `${who} took back someone's access`,
};

const article = (role: string) => `${/^[aeiou]/i.test(role) ? "an" : "a"} ${role}`;

/**
 * An audit entry in words: "sam@studio.com saved the class “Morning Flow”". `label` finds an
 * item's name from its type and id (the editor's list), falling back to the id.
 */
export function describeActivity(entry: AuditEntry, pack: Pack, label: (type: EntityType, id: string) => string | undefined): string {
  const who = entry.actorEmail && entry.actorEmail !== "cli" ? entry.actorEmail : "Someone";
  const type = entry.targetType as EntityType | null;
  const what = type && type in pack.nouns && entry.targetId
    ? `the ${pack.nouns[type].one.toLowerCase()} “${label(type, entry.targetId) ?? entry.targetId}”`
    : "an item";
  const words = ACTIONS[entry.action];
  return words ? words(who, what, entry) : `${who}: ${entry.action.replace(/[._]/g, " ")}`;
}
